import { P2Alert, P2Button, P2Modal, P2TextDescription, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script';
import ClockPendingTask from '../../../assets/svg/clock-pending-task';
import { Helpers } from '../../../utils/helper';
import SuccessIconStageE from '../../../assets/svg/success-icon-stage-e';
import { StageDEndorseFormInfo } from '../../../models/classes/stage-d/Review-Application.class';
import MoreDetailsWhiteIcon from '../../../assets/svg/more-details-white';
import SuccessGreyIcon from '../../../assets/svg/success-icon-grey';
import { useEffect, useMemo, useState } from 'react';
import { PolymerAxios } from '../../../utils/axios';
import { API_UpdateOccupierSpouseNotification } from '../../../utils/api';
import { useAppSelector } from '../../../redux/hook';
import { PolymerCrypto } from '../../../utils/encryptor';
import React from 'react';
import { LottieLoader } from '../../../components/loader';
import { useFormUpdater } from '../../../hooks/useFormUpdater';

interface ApplicationUpdateEndorseFormProps {
  data: StageDEndorseFormInfo[];
  onRefetch: () => void;
  loginUser: string;
  singpassToken: string;
  applicationUuid?: string | null | undefined;
  isLoading?: boolean;
  isUninitialized?: boolean;
}

function ApplicationUpdateEndorseForm({ data, onRefetch, ...props }: ApplicationUpdateEndorseFormProps) {
  const [modalDataIndex, setModalDataIndex] = useState<string>('');
  const [shareLinkModalVisible, setShareLinkModalVisible] = useState<boolean>(false);
  const [occupierEmailAddress, setOccupierEmailAddress] = useState<string>('');
  const [occupierEmailAddressError, setOccupierEmailAddressError] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [shareLinkAlerts, setShareLinkAlerts] = useState<{ [key: string]: boolean }>({});
  const [spouseName, setSpouseName] = useState<string>('');

  const [activeForms, setActiveForms] = useState<StageDEndorseFormInfo[]>([]);
  const [recentlySubmittedForms, setRecentlySubmittedForms] = useState<StageDEndorseFormInfo[]>([]);

  const applicationStep1 = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const loginUserSpouseUin = applicationStep1?.household?.incoming_owners?.find((owner) => owner.myinfo.uinfin === props.loginUser)?.spouse || '';

  const allForms = useMemo(() => [...data, ...recentlySubmittedForms].sort((a, b) => a.id - b.id), [data, recentlySubmittedForms]);

  // Use the form updater hook with StageDEndorseFormInfo type
  useFormUpdater<StageDEndorseFormInfo>({
    loginUser: props.loginUser,
    singpassToken: props.singpassToken,
    applicationUuid: props.applicationUuid,
    isUninitialized: props.isUninitialized,
    data: data,
    onRefetch,
  });

  useEffect(() => {
    if (activeForms.length === 0) return;
    // Create a Set of form IDs from data for O(1) lookups
    const formIdsInData = new Set(data.map((item) => item.id));
    // // Check if any active forms are missing from data
    const formsToRemove = activeForms.filter((form) => !formIdsInData.has(form.id));
    if (formsToRemove.length > 0) {
      handleSuccessEndorsement(formsToRemove);
    }
  }, [data, activeForms, recentlySubmittedForms]);

  /**
   * Spouse
   * Find spouse name based on application step 1 data.
   * Display alert to share link if occupier spouse has not sign form && owner has already signed
   *
   */
  useEffect(() => {
    if (allForms && loginUserSpouseUin) {
      // Check if login user has a spouse occupier in any of the form
      const initialShareLinkAlerts: { [key: string]: boolean } = {};
      allForms.forEach((item) => {
        const hasSpouse = item.occupiers.find(
          (occupier) => occupier.uinfin === loginUserSpouseUin && occupier?.endorsement_date === undefined && occupier?.notified !== true
        );
        const ownerSigned = item.owners.find((owner) => owner.uinfin === props.loginUser && owner.endorsement_date !== undefined);
        if (hasSpouse && ownerSigned) {
          setSpouseName(hasSpouse.name);
          initialShareLinkAlerts[item.signify_document_id] = true;
        }
      });
      setShareLinkAlerts(initialShareLinkAlerts);
    }
  }, [allForms, loginUserSpouseUin, props.loginUser]);

  const handleSuccessEndorsement = (forms: StageDEndorseFormInfo[]) => {
    forms.forEach((form) => {
      setRecentlySubmittedForms((prev) => [...prev, form]);
      setActiveForms((prev) => prev.filter((item) => item.id !== form.id));
      setTimeout(() => {
        setRecentlySubmittedForms((prev) => prev.filter((item) => item.id !== form.id));
      }, 5000);
    });
  };

  const updateNotifyAlertVisible = (id: string) => {
    setShareLinkAlerts((prevStates) => ({
      ...prevStates,
      [id]: !prevStates[id],
    }));
  };

  const lastIndexBelongToCurrentOwner = (): number => {
    // Find the last index where the current owner is included
    for (let i = allForms.length - 1; i >= 0; i--) {
      const currentOwner = allForms[i].owners.find((owner) => owner.uinfin === props.loginUser);
      if (currentOwner) {
        return i;
      }
    }
    return 0;
  };

  const currentOwnerIncluded = (index: number): boolean => {
    const currentOwner = allForms[index]?.owners?.find((owner) => owner.uinfin === props.loginUser);
    return currentOwner ? true : false;
  };

  const checkIfOwnerEndorsed = (index: number): boolean => {
    if (!allForms[index]) return false;
    const currentOwner = allForms[index].owners.find((owner) => owner.uinfin === props.loginUser);
    return !!(currentOwner?.endorsement_date !== undefined && currentOwner.endorsement_date !== '');
  };

  const openEndorseWindow = (signingLink: string, formId: number) => {
    window.open(signingLink, '_blank');
    const form = allForms.find((form) => form.id === formId);
    // Check in both activeForms and recentlySubmittedForms to prevent duplicates
    const alreadyTracked = activeForms.some((form) => form.id === formId) || recentlySubmittedForms.some((form) => form.id === formId);
    if (form && !alreadyTracked) {
      setActiveForms((prev) => [...prev, form]);
    }
  };

  const handleEmailChange = (emailAddr: string) => {
    setOccupierEmailAddressError(false);
    setOccupierEmailAddress(emailAddr);
  };

  const shareLinkModalClose = () => {
    setShareLinkModalVisible(false);
    setOccupierEmailAddress('');
  };

  const shareLinkModalSubmit = async () => {
    // Basic email validation, can improve regex if needed.
    const re = /\S+@\S+\.\S+/;
    setOccupierEmailAddressError(!re.test(occupierEmailAddress));

    // If valid email, call Polymer BE to send email.
    if (re.test(occupierEmailAddress)) {
      const payload = {
        occupier_uinfin: PolymerCrypto.encryption(loginUserSpouseUin),
        occupier_email: occupierEmailAddress,
        application_uuid: props.applicationUuid,
        signify_id: modalDataIndex,
      };

      try {
        setIsSendingEmail(true);
        const response = await PolymerAxios(props.loginUser ?? '', props.singpassToken).post(`${API_UpdateOccupierSpouseNotification}`, payload);
        if (response.status === 200) {
          onRefetch();
          updateNotifyAlertVisible(modalDataIndex);
        }
      } catch (e) {
        console.log(e);
        alert('An error occurred while sending the email. Please try again later.');
      }

      setIsSendingEmail(false);
      setOccupierEmailAddress('');
      setModalDataIndex('');
      shareLinkModalClose();
    }
  };

  return (
    <div className='polymer-stage-d-container__endorse-form'>
      {allForms.some((form) => form.signify_status === 'draft') ? (
        <P2TextDescription color='#000' title='Forms' size='1.5rem' />
      ) : (
        <P2Alert variant='warning' text='HDB is reviewing your application. We will let you know if there are any updates on the application.' />
      )}

      <div style={{ paddingTop: '16px' }}>
        {allForms.map((item, index) => {
          const combinedOwnerOccupier = [...item.owners, ...item.occupiers];
          const isRecentlySubmitted = recentlySubmittedForms.find((recent) => item.id === recent.id);

          // Render success alert if form has been signed and was recently submitted.
          if (isRecentlySubmitted) {
            return (
              <div key={item.id} className='my-2'>
                <div className='polymer-stage-d-container__subtitle my-2'>
                  <P2TextLabel fontSize='1.25rem' bold={700} label={item.title} />
                </div>
                <div className='my-3'>
                  <P2Alert withBorder variant='success' text='Form has been successfully sent to HDB!' />
                </div>
                {/* Divider */}
                {index !== lastIndexBelongToCurrentOwner() && (
                  <div style={{ padding: '24px 0' }}>
                    <div style={{ borderBottom: '1px dashed #0C8188' }} />
                  </div>
                )}
              </div>
            );
          }

          // Only render the form details if the current owner is included and status is "draft"
          if (currentOwnerIncluded(index) && item.signify_status === 'draft') {
            return (
              <div key={item.id}>
                {/* Form Header */}
                <div className='polymer-stage-d-container__subtitle'>
                  <P2TextLabel fontSize='1.25rem' bold={700} label={item.title} />
                  <div className='polymer-stage-d-container__subtitle__date'>
                    <ClockPendingTask />
                    <p>Received on {Helpers.formatDate(item.received_date)}</p>
                  </div>
                </div>

                {/* Endorse Form Button */}
                <div className='polymer-stage-d-container__endorse-form-button-container'>
                  <P2TextLabel fontSize='1.125rem' bold={400} label='Please endorse the following form(s) via Singpass.' />
                  <P2Button
                    variant='button-icon'
                    endIcon={<MoreDetailsWhiteIcon />}
                    width='100%'
                    disabled={checkIfOwnerEndorsed(index)}
                    onClick={() => openEndorseWindow(item.signing_link, item.id)}>
                    Endorse form
                  </P2Button>
                </div>

                {/* Owner/Occupier Status */}
                <div className='polymer-stage-d-container__endorse-form-owners-container'>
                  {combinedOwnerOccupier.map((owner, ownerIndex) => (
                    <React.Fragment key={`${item.id}-${ownerIndex}`}>
                      <div className='polymer-stage-d-container__endorse-form-owner'>
                        <P2TextLabel fontSize='1.125rem' bold={400} label={owner.name} />
                        <div className='polymer-stage-d-container__endorse-form-owner-endorsement'>
                          {!owner.endorsement_date || owner.endorsement_date === '' ? (
                            <>
                              <SuccessGreyIcon />
                              {owner.notified ? (
                                <P2TextLabel fontSize='1.125rem' bold={400} label='Link shared to occupier' />
                              ) : (
                                <P2TextLabel fontSize='1.125rem' bold={400} label='Awaiting endorsement' />
                              )}
                            </>
                          ) : (
                            <>
                              <SuccessIconStageE />
                              <P2TextLabel fontSize='1rem' bold={700} color='#00563A' label={Helpers.formatDate(owner.endorsement_date)} />
                            </>
                          )}
                        </div>
                      </div>
                      {ownerIndex + 1 !== combinedOwnerOccupier.length && (
                        <div style={{ padding: '16px 0' }}>
                          <div style={{ borderBottom: '1px dashed #0C8188' }} />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Share Link Alert */}
                {shareLinkAlerts[item.signify_document_id] && (
                  <div className='mt-3'>
                    <P2Alert
                      withBorder
                      variant='warning'
                      title='Share the form for endorsement'
                      text={
                        <div>
                          Send the form to {spouseName} by {' '}
                          <a
                            onClick={() => {
                              setShareLinkModalVisible(true);
                              setModalDataIndex(item.signify_document_id.toString());
                            }}
                            rel='noreferrer'
                            className='polymer-text-links'>
                            providing their email address.
                          </a>
                        </div>
                      }
                    />
                  </div>
                )}

                {/* Divider */}
                {index !== lastIndexBelongToCurrentOwner() && (
                  <div style={{ padding: '24px 0' }}>
                    <div style={{ borderBottom: '1px dashed #0C8188' }} />
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Share Link Modal */}
      <div className='polymer-stage-d-container__endorse-modal-container'>
        {isSendingEmail ? (
          <div className='polymer-stage-d-container__endorse-modal-container'>
            <P2Modal isOpen={isSendingEmail} variant='default'>
              <LottieLoader />
            </P2Modal>
          </div>
        ) : (
          <P2Modal
            isOpen={shareLinkModalVisible}
            variant='default'
            title='Share the form for endorsement'
            primaryButtonText='Cancel'
            secondaryButtonText='Share'
            primaryButtonFunction={shareLinkModalClose}
            secondaryButtonFunction={shareLinkModalSubmit}>
            <>
              <p>Send the form to {spouseName} by providing their email address.</p>
              <P2TextInput
                isError={occupierEmailAddressError}
                errorMsg='Please enter a valid email address'
                value={occupierEmailAddress}
                label={`${spouseName} email address`}
                placeholder='email address'
                type='text'
                onChange={handleEmailChange}
              />
            </>
          </P2Modal>
        )}
      </div>
    </div>
  );
}

export default ApplicationUpdateEndorseForm;
